import { useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { TASK_TYPES, PRIORITY_LEVELS, TASK_DOMAINS, TASK_STATUSES } from "../lib/tasks";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "./ui/sheet";
import { ResponsivePopover as Popover, ResponsivePopoverTrigger as PopoverTrigger, ResponsivePopoverContent as PopoverContent } from "./ui/responsive-popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { toast } from "sonner";
import { Loader2, Building2, Search as SearchIcon, ChevronDown } from "lucide-react";

const MIN_PROSPECT_QUERY = 2;

const EMPTY_FORM = {
  title: "", description: "", due_date: "", task_type: "autre", priority: "medium", domain: "", status: "a_faire",
  cancel_reason: "", prospect: null, assigned_to: [],
};

// Multi-select popover for the responsable(s), same Popover+Checkbox pattern
// as AcquisitionChannelField.jsx, over a directory of users.
function AssigneesField({ value, onChange, directory, currentUserId, disabled }) {
  const selected = value || [];
  const nameFor = (u) => (u.id === currentUserId ? "Moi" : u.name);
  const triggerLabel = selected.length === 0
    ? "Sélectionner..."
    : selected.map((id) => directory.find((u) => u.id === id)).filter(Boolean).map(nameFor).join(", ");
  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" disabled={disabled}
          data-testid="task-form-assigned" className="w-full justify-between font-normal"
        >
          <span className="truncate text-left text-slate-700">{triggerLabel}</span>
          <ChevronDown size={14} className="ml-2 text-slate-400 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="space-y-0.5 max-h-56 overflow-y-auto scrollbar-thin">
          {directory.length === 0 ? (
            <div className="py-4 text-center text-xs text-slate-400">Aucun utilisateur disponible.</div>
          ) : directory.map((u) => (
            <label
              key={u.id} data-testid={`toggle-assignee-${u.id}`}
              className="flex items-center gap-2.5 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer"
            >
              <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
              <span className="text-sm text-slate-700">{nameFor(u)}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Prospect search-as-you-type combobox, reused by the tasks filter bar
// (Tasks.jsx) and by this form's optional prospect picker below.
export function ProspectFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < MIN_PROSPECT_QUERY) { setResults([]); return; }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: trimmed } });
        setResults(data.prospects || []);
      } catch (e) {
        // best-effort search-as-you-type
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start font-normal" data-testid="filter-prospect">
          <SearchIcon size={14} className="mr-2 text-slate-400 shrink-0" />
          <span className="truncate">{value ? value.pharmacy_name : "Rechercher un prospect…"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Nom de la pharmacie…" />
          <CommandList>
            {value && (
              <CommandItem onSelect={() => { onChange(null); setOpen(false); }} className="text-slate-500">
                Effacer le filtre
              </CommandItem>
            )}
            {query.trim().length < MIN_PROSPECT_QUERY ? (
              <div className="py-6 text-center text-xs text-slate-400">Tapez au moins {MIN_PROSPECT_QUERY} caractères.</div>
            ) : loading ? (
              <div className="py-6 text-center text-xs text-slate-400">Recherche…</div>
            ) : results.length === 0 ? (
              <CommandEmpty>Aucun prospect trouvé</CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((p) => (
                  <CommandItem key={p.id} value={p.id} onSelect={() => { onChange(p); setOpen(false); }}>
                    <Building2 size={14} className="mr-2 text-slate-400" />
                    {p.pharmacy_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Shared create/edit drawer for tasks (Tâches page and ProspectDetail's Tâches
// tab), so the two never drift into incompatible flows. `lockedProspect` fixes
// the prospect at creation (no picker) for callers that already have it.
export default function TaskFormSheet({ open, onOpenChange, task, canScopeAll, canEdit, directory, onSaved, onComplete, initialType, lockedProspect, initialValues }) {
  const isEdit = !!task;
  const { user: currentUser } = useAuth();
  // Creating a task away from yourself stays a scope_all-only trust
  // boundary (unchanged); editing an already-open task's responsable list
  // only needs tasks:edit, since check_task_scope on the backend already
  // guarantees the task is in the caller's own scope.
  const canManageAssignees = isEdit ? !!canEdit : !!canScopeAll;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title, description: task.description || "", due_date: task.due_date || "",
        task_type: task.task_type || "autre", priority: task.priority || "medium", domain: task.domain || "",
        status: task.status || (task.done ? "termine" : "a_faire"),
        cancel_reason: task.cancel_reason || "",
        // Placeholder until the real pharmacy_name loads below: GET /tasks returns
        // only prospect_id, never the prospect's name.
        prospect: task.prospect_id ? { id: task.prospect_id, pharmacy_name: task.prospect_id } : null,
        assigned_to: task.assigned_to || [],
      });
      if (task.prospect_id) {
        api.get(`/prospects/${task.prospect_id}`)
          .then(({ data }) => setForm((f) => ({ ...f, prospect: { id: data.id, pharmacy_name: data.pharmacy_name } })))
          .catch(() => {}); // keep the id-as-placeholder fallback already set above
      }
    } else {
      // `initialValues` prefills a creation (e.g. a follow-up task opened from a checklist anomaly).
      setForm({ ...EMPTY_FORM, task_type: initialType || "autre", prospect: lockedProspect || null, ...(initialValues || {}) });
    }
  }, [open, task, initialType, lockedProspect, initialValues]);

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    // Domain is mandatory on creation only (backend TaskCreate.domain has no
    // default): an older task may keep domain=null through an unrelated edit.
    if (!isEdit && !form.domain) {
      toast.error("Le domaine est requis");
      return;
    }
    // On edit, an empty picker means every responsable was unchecked; a task must
    // keep at least one (the backend enforces it too, this saves a round-trip).
    if (isEdit && canManageAssignees && form.assigned_to.length === 0) {
      toast.error("Sélectionnez au moins un responsable");
      return;
    }
    // Mirrors the prospect "Perdu" flow's loss_reason requirement; the backend
    // also enforces it (400), checked here first to save a round-trip.
    if (isEdit && form.status === "annulee" && !form.cancel_reason.trim()) {
      toast.error("Le motif d'annulation est requis");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          title: form.title, description: form.description, due_date: form.due_date || null,
          task_type: form.task_type, priority: form.priority, status: form.status,
          // Explicit null (not omitted) detaches the prospect; omitting it would
          // leave the link untouched.
          prospect_id: form.prospect?.id || null,
        };
        // Only sent when cancelling: the backend clears any existing reason once
        // status moves away from "annulee".
        if (form.status === "annulee") payload.cancel_reason = form.cancel_reason.trim();
        if (canManageAssignees) payload.assigned_to = form.assigned_to;
        // A legacy task's domain can stay unset through an edit that
        // doesn't touch it; only send it once actually chosen.
        if (form.domain) payload.domain = form.domain;
        if (form.status === "termine" && task.status !== "termine") {
          onComplete(task, payload);
          return;
        }
        const { data } = await api.patch(`/tasks/${task.id}`, payload);
        toast.success("Tâche mise à jour");
        onSaved(data, false);
      } else {
        const payload = {
          title: form.title, description: form.description, due_date: form.due_date || null,
          task_type: form.task_type, priority: form.priority, domain: form.domain,
          prospect_id: (lockedProspect || form.prospect)?.id || null,
        };
        // Leaving the picker empty at creation keeps the previous
        // "no explicit choice -> server defaults to me" behavior.
        if (canManageAssignees && form.assigned_to.length > 0) payload.assigned_to = form.assigned_to;
        const { data } = await api.post("/tasks", payload);
        toast.success("Tâche créée");
        onSaved(data, true);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="task-form-sheet">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Modifier la tâche" : "Nouvelle tâche"}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-6">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Titre</label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="task-form-title" autoFocus />
          </div>
          {!isEdit && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Prospect{lockedProspect ? "" : " (optionnel)"}</label>
              {lockedProspect ? (
                <div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700">
                  <Building2 size={14} className="mr-2 text-slate-400 shrink-0" />
                  {lockedProspect.pharmacy_name}
                </div>
              ) : (
                <ProspectFilter value={form.prospect} onChange={(p) => setForm((f) => ({ ...f, prospect: p }))} />
              )}
            </div>
          )}
          {isEdit && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Prospect (optionnel)</label>
              <ProspectFilter value={form.prospect} onChange={(p) => setForm((f) => ({ ...f, prospect: p }))} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Type d'action</label>
              <Select value={form.task_type} onValueChange={(v) => setForm((f) => ({ ...f, task_type: v }))}>
                <SelectTrigger data-testid="task-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Priorité</label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="task-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_LEVELS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isEdit && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Statut</label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="task-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {isEdit && form.status === "annulee" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Motif d'annulation</label>
              <Textarea
                value={form.cancel_reason} onChange={(e) => setForm((f) => ({ ...f, cancel_reason: e.target.value }))}
                rows={2} placeholder="Pourquoi cette tâche est-elle annulée ?" data-testid="task-form-cancel-reason"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Domaine{isEdit ? " (optionnel)" : ""}</label>
            <Select value={form.domain} onValueChange={(v) => setForm((f) => ({ ...f, domain: v }))}>
              <SelectTrigger data-testid="task-form-domain"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
              <SelectContent>
                {TASK_DOMAINS.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Échéance</label>
            <Input type="date" value={form.due_date || ""} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="task-form-due" />
          </div>
          {canManageAssignees && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Responsable(s)</label>
              <AssigneesField
                value={form.assigned_to}
                onChange={(v) => setForm((f) => ({ ...f, assigned_to: v }))}
                directory={directory}
                currentUserId={currentUser?.id}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Description</label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} data-testid="task-form-description" />
          </div>
        </div>
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={save}
            disabled={saving || !form.title.trim() || (!isEdit && !form.domain) || (isEdit && form.status === "annulee" && !form.cancel_reason.trim())}
            data-testid="task-form-save"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : isEdit ? "Enregistrer" : "Créer"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
